import { clearUser, setUser, setUserLoading } from "../store/userReducer"
import { getReferralId } from "./referralHelper"
import { generateReferralCode } from "./referralUtils"
import { API_URL } from "../constants/constants"
import {
  getDecimals,
  getLockedZtd,
  getUsdtContract,
  getZtd,
} from "../data/smart"
import { ethers } from "ethers"

// Функция для определения мобильного устройства
export const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  )
}

// Функция для проверки наличия MetaMask
export const isMetaMaskInstalled = () => {
  return typeof window.ethereum !== "undefined" && window.ethereum.isMetaMask
}

// Функция для проверки разблокированности MetaMask
export const isMetaMaskUnlocked = async () => {
  try {
    if (!isMetaMaskInstalled()) {
      return false
    }
    const accounts = await window.ethereum.request({ method: "eth_accounts" })
    return accounts.length > 0
  } catch (error) {
    console.error("Error checking MetaMask unlock status:", error)
    return false
  }
}

// Функция для получения статуса MetaMask
export const getMetaMaskStatus = async () => {
  const installed = isMetaMaskInstalled()

  if (!installed) {
    return {
      installed: false,
      unlocked: false,
      hasAccounts: false,
    }
  }

  try {
    const accounts = await window.ethereum.request({ method: "eth_accounts" })
    const unlocked = accounts.length > 0

    return {
      installed: true,
      unlocked: unlocked,
      hasAccounts: unlocked,
      accounts: accounts,
    }
  } catch (error) {
    console.error("Error getting MetaMask status:", error)
    return {
      installed: true,
      unlocked: false,
      hasAccounts: false,
    }
  }
}

// Функция для создания глубокой ссылки на MetaMask
const openMetaMaskMobile = () => {
  window.location.href = "https://metamask.app.link/dapp/zombietd.app/"
}

export const connectHandler = async (dispatch) => {
  try {
    // Проверяем, есть ли MetaMask
    if (typeof window.ethereum !== "undefined") {
      // Если это не мобильное устройство, проверяем разблокированность MetaMask
      if (!isMobile()) {
        const isUnlocked = await isMetaMaskUnlocked()
        if (!isUnlocked) {
          try {
            await window.ethereum.request({ method: "eth_requestAccounts" })
          } catch (unlockError) {
            if (unlockError.code === 4001) {
              return null
            }
            throw unlockError
          }
        }
      }

      dispatch(setUserLoading(true))

      try {
        // Запрашиваем аккаунты у MetaMask
        const accounts = await window.ethereum.request({
          method: "eth_requestAccounts",
        })

        const address = accounts[0]

        // Получаем referral ID из localStorage
        const referralId = getReferralId()

        // Генерируем хеш текущего адреса пользователя
        const walletHash = generateReferralCode(address)

        try {
          // Отправляем запрос на сервер для создания сессии
          const response = await fetch(`${API_URL}/auth/login`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              wallet_address: address,
              referral_id: referralId || "",
              wallet_hash: walletHash,
            }),
          })

          const data = await response.json()

          if (data.success) {
            // Сохраняем в localStorage
            localStorage.setItem("walletAddress", address)
            localStorage.setItem("sessionToken", data.token)

            // Обновляем стейт с данными пользователя
            dispatch(
              setUser({
                address: address,
                sessionToken: data.token,
                userId: data.user?.userId || null,
                walletConnected: true,
                ztd: await getZtd(address),
                lockedZtd: await getLockedZtd(address),
              }),
            )

            return [address, data.zealy_reward]
          } else {
            throw new Error(data.error || "Login failed")
          }
        } catch (serverError) {
          console.error("Server error:", serverError)
          // Если сервер недоступен, сохраняем только адрес
          localStorage.setItem("walletAddress", address)
          dispatch(
            setUser({
              address: address,
              walletConnected: true,
            }),
          )
          return address
        }
      } catch (requestError) {
        // Если ошибка связана с отклонением пользователем
        if (requestError.code === 4001) {
          dispatch(setUserLoading(false))
          return null
        }

        // Если на мобильном устройстве и возникла ошибка, пробуем глубокую ссылку
        if (isMobile()) {
          openMetaMaskMobile()
          dispatch(setUserLoading(false))
          return null
        }

        throw requestError
      }
    } else {
      // Если MetaMask не найден
      if (isMobile()) {
        // На мобильном устройстве перенаправляем в MetaMask
        alert("MetaMask не найден. Переходим в приложение MetaMask...")
        openMetaMaskMobile()
        return null
      } else {
        alert("Please install MetaMask!")
        return null
      }
    }
  } catch (error) {
    console.error("Error connecting to MetaMask:", error)
    dispatch(setUserLoading(false))
    return null
  }
}

export const disconnectHandler = async (dispatch, sessionToken = null) => {
  try {
    // Пытаемся отключить MetaMask, если доступен
    if (window.ethereum) {
      try {
        await window.ethereum.request({
          method: "wallet_revokePermissions",
          params: [{ eth_accounts: {} }],
        })
      } catch (error) {
        console.warn("MetaMask disconnect error:", error)
        // Игнорируем ошибки отключения MetaMask
      }
    }

    if (sessionToken) {
      // Отправляем запрос на выход, если есть токен
      try {
        await fetch(`${API_URL}/auth/logout`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${sessionToken}`,
          },
        })
      } catch (error) {
        console.warn("Server logout error:", error)
        // Игнорируем ошибки сервера при выходе
      }
    }

    // Очищаем локальное хранилище
    localStorage.removeItem("walletAddress")
    localStorage.removeItem("sessionToken")
    dispatch(clearUser())

    // Возвращаем результат успешного выхода
    return { success: true }
  } catch (error) {
    console.error("Logout error:", error)

    // Даже при ошибке очищаем локальные данные
    localStorage.removeItem("walletAddress")
    localStorage.removeItem("sessionToken")
    dispatch(clearUser())

    return { success: true, error: error.message }
  }
}

export const restoreSession = async (dispatch) => {
  const savedAddress = localStorage.getItem("walletAddress")
  const savedToken = localStorage.getItem("sessionToken")

  if (savedAddress) {
    dispatch(setUserLoading(true))
    try {
      // Проверяем валидность сессии на сервере
      const sessionData = await validateSession(savedToken)

      if (sessionData.success) {
        // Проверяем статус MetaMask
        const metaMaskStatus = await getMetaMaskStatus()

        if (metaMaskStatus.installed && metaMaskStatus.unlocked) {
          // MetaMask установлен и разблокирован - проверяем аккаунт
          const accounts = await window.ethereum.request({
            method: "eth_accounts",
          })

          if (
            accounts.length > 0 &&
            accounts[0].toLowerCase() === savedAddress.toLowerCase()
          ) {
            // Аккаунт совпадает - восстанавливаем сессию
            dispatch(
              setUser({
                address: savedAddress,
                sessionToken: savedToken,
                userId: sessionData.user?.userId || null,
                ztd: await getZtd(savedAddress),
                lockedZtd: await getLockedZtd(savedAddress),
                walletConnected: true,
              }),
            )
          } else {
            // Аккаунт не совпадает или отключен - очищаем
            await disconnectHandler(dispatch, savedToken)
          }
        } else {
          // MetaMask заблокирован или не установлен, но сессия валидна
          // Сохраняем пользователя, но показываем, что кошелек недоступен
          dispatch(
            setUser({
              address: savedAddress,
              sessionToken: savedToken,
              userId: sessionData.user?.userId || null,
              walletConnected: false,
            }),
          )
        }
      } else {
        // Сессия невалидна - очищаем
        await disconnectHandler(dispatch, savedToken)
      }
    } catch (error) {
      console.error("Session restoration error:", error)
      await disconnectHandler(dispatch, savedToken)
    } finally {
      dispatch(setUserLoading(false))
    }
  } else {
    // Нет сохраненной сессии - убедимся, что состояние очищено
    dispatch(setUserLoading(false))
  }
}

// Улучшенная функция для завершения сессии при закрытии браузера
export const setupSessionCleanup = () => {
  let isRefreshing = false

  const handleBeforeUnload = (event) => {
    // Проверяем, является ли это обновлением страницы
    const navigation = performance.getEntriesByType("navigation")[0]
    if (navigation && navigation.type === "reload") {
      isRefreshing = true
      return
    }

    // Только при закрытии браузера/вкладки очищаем сессию
    const sessionToken = localStorage.getItem("sessionToken")
    if (sessionToken && !isRefreshing) {
      // Используем sendBeacon для надежной отправки при закрытии страницы
      const data = JSON.stringify({
        action: "logout",
        sessionToken: sessionToken,
      })

      const blob = new Blob([data], { type: "application/json" })

      const success = navigator.sendBeacon(`${API_URL}/auth/logout`, blob)

      // Альтернативно используем синхронный fetch как fallback
      if (!success && !navigator.sendBeacon) {
        try {
          fetch(`${API_URL}/auth/logout`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${sessionToken}`,
            },
            body: data,
            keepalive: true,
          })
        } catch (error) {
          console.error("Error during session cleanup:", error)
        }
      }

      // Очищаем локальные данные только при закрытии браузера
      localStorage.removeItem("walletAddress")
      localStorage.removeItem("sessionToken")
    }
  }

  // Обработчик для отслеживания перехода на другую страницу
  const handleVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      // Страница скрыта, но это может быть переход на другую вкладку
      // Не очищаем данные здесь
    }
  }

  // Добавляем обработчики событий
  window.addEventListener("beforeunload", handleBeforeUnload)
  window.addEventListener("pagehide", handleBeforeUnload)
  document.addEventListener("visibilitychange", handleVisibilityChange)

  // Возвращаем функцию для удаления обработчиков
  return () => {
    window.removeEventListener("beforeunload", handleBeforeUnload)
    window.removeEventListener("pagehide", handleBeforeUnload)
    document.removeEventListener("visibilitychange", handleVisibilityChange)
  }
}

export const validateSession = async (sessionToken) => {
  try {
    const response = await fetch(`${API_URL}/auth/validate-session`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${sessionToken}`,
      },
    })

    return await response.json()
  } catch (error) {
    console.error("Session validation error:", error)
    return { valid: false }
  }
}

export const handleAccountsChanged = (accounts, dispatch) => {
  if (accounts.length === 0) {
    // Пользователь отключил кошелек
    disconnectHandler(dispatch)
  } else {
    // Пользователь сменил аккаунт
    const newAddress = accounts[0]
    const savedAddress = localStorage.getItem("walletAddress")

    if (
      savedAddress &&
      newAddress.toLowerCase() !== savedAddress.toLowerCase()
    ) {
      handleAccountChange(newAddress, dispatch)
    }
  }
}

export const handleAccountChange = async (newAddress, dispatch) => {
  try {
    // Создаем новую сессию для нового аккаунта
    const response = await fetch(`${API_URL}/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ wallet_address: newAddress }),
    })

    const data = await response.json()

    if (data.success) {
      localStorage.setItem("walletAddress", newAddress)
      localStorage.setItem("sessionToken", data.token)

      dispatch(
        setUser({
          address: newAddress,
          sessionToken: data.sessionToken,
          userId: data.user?.userId || null,
          walletConnected: true,
        }),
      )
    } else {
      throw new Error(data.error || "Account switch failed")
    }
  } catch (error) {
    console.error("Error switching account:", error)
    // Если сервер недоступен, все равно обновляем адрес
    localStorage.setItem("walletAddress", newAddress)
    dispatch(
      setUser({
        address: newAddress,
        walletConnected: true,
      }),
    )
  }
}
